import React, { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  IconButton,
  Typography,
  Box,
  Autocomplete,
  CircularProgress,
  Divider,
  Chip,
  Alert,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";

const EditModal = ({
  open,
  onClose,
  item,
  group_code,
  project_code,
  apiUrl,
  onSave,
  semiProductMode,
}) => {
  const [materials, setMaterials] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [items, setItems] = useState([]);
  const [validationError, setValidationError] = useState("");

  useEffect(() => {
    if (open && item) {
      loadMaterials();
      loadExistingBomItems();
      setValidationError("");
    }
  }, [open, item]);

  const loadMaterials = async () => {
    setLoading(true);
    try {
      const endpoint = semiProductMode ? "bringSemi" : "bringAll";
      const response = await axios.get(
        `${apiUrl}/api/material/${endpoint}?group_code=${group_code}&project_code=${project_code}`
      );
      setMaterials(response.data || []);
    } catch (error) {
      console.error("Error loading materials:", error);
      setValidationError("자재 목록을 불러오는데 실패했습니다.");
    } finally {
      setLoading(false);
    }
  };

  // 사내 품명(material_grouping)이 있는 것만 그룹으로 사용
  const groupedMaterials = useMemo(() => {
    const map = new Map();
    materials
      .filter((m) => m.material_grouping) // null/빈값인 자재는 그룹화하지 않음
      .forEach((m) => {
        const key = m.material_grouping;
        if (!map.has(key)) map.set(key, []);
        map.get(key).push(m);
      });

    return Array.from(map.entries()).map(([material_grouping, list]) => ({
      material_grouping,
      materials: list,
    }));
  }, [materials]);

  const loadExistingBomItems = async () => {
    try {
      const existingItems = item.existingBomItems || [];
      if (existingItems.length > 0) {
        const formattedItems = existingItems.map((bomItem) => ({
          id: `existing_${bomItem.material_id}`,
          material_id: bomItem.material_id,
          material_name: bomItem.material_name || bomItem.item_name,
          quantity: bomItem.quantity || "1",
          qty_unit: bomItem.qty_unit || bomItem.default_unit || "EA",
          isExisting: true,
          isSemi: bomItem.category === "semi" || bomItem.item_type === 0,
        }));
        setItems(formattedItems);
      } else {
        setItems([createNewItem()]);
      }
    } catch (e) {
      setItems([createNewItem()]);
    }
  };

  const createNewItem = () => ({
    id: `new_${Date.now()}`,
    material_id: "",
    material_name: "",
    quantity: "1",
    qty_unit: "EA",
    isExisting: false,
    isSemi: false,
  });

  const handleAddItem = () => {
    setItems((prev) => [...prev, createNewItem()]);
    setValidationError("");
  };

  const handleDeleteItem = (id) => {
    if (items.length <= 1) return; // 최소 1개 유지
    setItems((prev) => prev.filter((item) => item.id !== id));
    setValidationError("");
  };

  const handleItemChange = (id, field, value) => {
    setItems((prev) =>
      prev.map((item) => {
        if (item.id === id) {
          if (field === "material_id") {
            const selectedMaterial = materials.find(
              (m) => m.material_id === value
            );
            return {
              ...item,
              [field]: value,
              material_name:
                selectedMaterial?.material_name ||
                selectedMaterial?.item_name ||
                "",
              isSemi:
                selectedMaterial?.category === "semi" ||
                selectedMaterial?.item_type === 0,
              qty_unit: selectedMaterial?.default_unit || "EA",
            };
          }
          return { ...item, [field]: value };
        }
        return item;
      })
    );
    setValidationError("");
  };

  const validateItems = () => {
    if (items.length === 0) {
      return "최소 1개 이상의 자재를 등록해야 합니다.";
    }
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      if (!item.material_id) {
        return `${i + 1}번째 항목: 자재를 선택해주세요.`;
      }
      if (!item.quantity || item.quantity === "") {
        return `${i + 1}번째 항목: 수량을 입력해주세요.`;
      }
      const quantityNum = parseFloat(item.quantity);
      if (isNaN(quantityNum)) {
        return `${i + 1}번째 항목: 수량은 숫자여야 합니다.`;
      }
      if (quantityNum <= 0) {
        return `${i + 1}번째 항목: 수량은 0보다 커야 합니다.`;
      }
      if (!item.qty_unit || item.qty_unit.trim() === "") {
        return `${i + 1}번째 항목: 단위를 입력해주세요.`;
      }
    }
    const materialIds = items.map((item) => item.material_id);
    const uniqueMaterialIds = new Set(materialIds);
    if (uniqueMaterialIds.size !== items.length) {
      const duplicates = materialIds.filter(
        (id, index) => materialIds.indexOf(id) !== index
      );
      const duplicateMaterial = materials.find(
        (m) => m.material_id === duplicates[0]
      );
      return `중복된 자재가 있습니다: ${
        duplicateMaterial?.material_name ||
        duplicateMaterial?.item_name ||
        "알 수 없음"
      }`;
    }
    return null;
  };

  const handleSave = async () => {
    const validationErrorMsg = validateItems();
    if (validationErrorMsg) {
      setValidationError(validationErrorMsg);
      return;
    }
    setSaving(true);
    setValidationError("");
    try {
      const endpoint = semiProductMode ? "updateSemiAll" : "updateAll";
      const url = `${apiUrl}/api/bom/${endpoint}`;

      const payload = {
        group_code,
        project_code,
        item_id: item.parent_item_id,
        data: items.map((item) => ({
          material_id: item.material_id,
          quantity: parseFloat(item.quantity),
          qty_unit: item.qty_unit.trim(),
        })),
      };
      const response = await axios.post(url, payload);
      if (response.data.success) {
        onSave({
          success: true,
          message: response.data.message || "BOM이 업데이트되었습니다.",
          item_id: response.data.item_id,
        });
        onClose();
      } else {
        setValidationError(response.data.message || "업데이트에 실패했습니다.");
      }
    } catch (error) {
      let errorMessage = "BOM 업데이트 중 오류가 발생했습니다.";
      if (error.response) {
        if (error.response.data?.message) {
          errorMessage = error.response.data.message;
        } else if (error.response.status === 400) {
          errorMessage = "잘못된 요청입니다. 입력값을 확인해주세요.";
        } else if (error.response.status === 404) {
          errorMessage = "제품을 찾을 수 없습니다.";
        } else if (error.response.status === 500) {
          errorMessage = "서버 오류가 발생했습니다. 잠시 후 다시 시도해주세요.";
        }
      } else if (error.request) {
        errorMessage = "서버와 연결할 수 없습니다. 네트워크를 확인해주세요.";
      }
      setValidationError(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          pb: 1,
        }}
      >
        <Typography variant="h6">
          BOM 관리 -{" "}
          {item?.parent_material_grouping
            ? item.parent_material_grouping
            : item?.parent_item_name || ""}
          {semiProductMode && " (반제품 모드)"}
        </Typography>
        <IconButton edge="end" onClick={onClose} disabled={saving}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ pt: 2 }}>
        {validationError && (
          <Alert
            severity="error"
            sx={{ mb: 2 }}
            onClose={() => setValidationError("")}
          >
            {validationError}
          </Alert>
        )}

        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary">
            기존 자재를 수정하거나 새로운 자재를 추가할 수 있습니다.
          </Typography>
        </Box>

        {/* 사내 품명(그룹) 단위 추가용 Autocomplete */}
        <Box sx={{ mb: 2 }}>
          <Autocomplete
            options={groupedMaterials}
            getOptionLabel={(option) => option.material_grouping || ""}
            onChange={(_, newGroup) => {
              if (!newGroup) return;
              setItems((prev) => {
                const groupMaterialIds = newGroup.materials.map(
                  (m) => m.material_id
                );
                // 기존 items 에서 이 그룹에 속한 자재들 제거
                const filtered = prev.filter(
                  (it) => !groupMaterialIds.includes(it.material_id)
                );
                // 그룹에 속한 자재들을 row 로 추가
                const newRows = newGroup.materials.map((m) => ({
                  id: `group_${newGroup.material_grouping}_${m.material_id}`,
                  material_id: m.material_id,
                  material_name:
                    m.material_grouping || m.material_name || m.item_name,
                  quantity: "1",
                  qty_unit: m.default_unit || "EA",
                  isExisting: false,
                  isSemi: m.category === "semi" || m.item_type === 0,
                }));
                return [...filtered, ...newRows];
              });
              setValidationError("");
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="사내 품명(그룹)으로 추가"
                placeholder="사내 품명을 검색해서 그룹 단위로 추가"
                size="small"
                disabled={saving || loading}
              />
            )}
          />
        </Box>

        {loading ? (
          <Box display="flex" justifyContent="center" my={2}>
            <CircularProgress size={24} />
          </Box>
        ) : (
          <>
            {items.map((itemData) => (
              <Box
                key={itemData.id}
                sx={{
                  border: "1px solid #eee",
                  borderRadius: 1,
                  p: 1.5,
                  mb: 1,
                }}
              >
                <Grid container spacing={1} alignItems="center">
                  {/* 자재 선택 (개별 material 선택용 Autocomplete) */}
                  <Grid item xs={6} md={5}>
                    <Autocomplete
                      options={materials}
                      getOptionLabel={(option) =>
                        option.material_grouping ||
                        option.material_name ||
                        option.item_name ||
                        ""
                      }
                      isOptionEqualToValue={(option, value) =>
                        option.material_id === value.material_id
                      }
                      onChange={(_, newValue) =>
                        handleItemChange(
                          itemData.id,
                          "material_id",
                          newValue?.material_id || ""
                        )
                      }
                      value={
                        materials.find(
                          (m) => m.material_id === itemData.material_id
                        ) || null
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="자재 선택"
                          size="small"
                          disabled={saving}
                        />
                      )}
                    />
                  </Grid>

                  {/* 수량 */}
                  <Grid item xs={3} md={2}>
                    <TextField
                      label="수량"
                      size="small"
                      value={itemData.quantity}
                      onChange={(e) =>
                        handleItemChange(
                          itemData.id,
                          "quantity",
                          e.target.value
                        )
                      }
                      type="number"
                      InputProps={{
                        inputProps: { min: 0.01, step: 0.01 },
                      }}
                      error={
                        !itemData.quantity ||
                        isNaN(parseFloat(itemData.quantity)) ||
                        parseFloat(itemData.quantity) <= 0
                      }
                      helperText={
                        !itemData.quantity
                          ? "수량을 입력하세요"
                          : isNaN(parseFloat(itemData.quantity))
                          ? "숫자를 입력하세요"
                          : parseFloat(itemData.quantity) <= 0
                          ? "0보다 큰 값"
                          : ""
                      }
                      disabled={saving}
                      fullWidth
                    />
                  </Grid>

                  {/* 단위 */}
                  <Grid item xs={3} md={2}>
                    <TextField
                      label="단위"
                      size="small"
                      value={itemData.qty_unit}
                      onChange={(e) =>
                        handleItemChange(
                          itemData.id,
                          "qty_unit",
                          e.target.value
                        )
                      }
                      error={
                        !itemData.qty_unit || itemData.qty_unit.trim() === ""
                      }
                      helperText={
                        !itemData.qty_unit || itemData.qty_unit.trim() === ""
                          ? "단위 입력"
                          : ""
                      }
                      disabled={saving}
                      fullWidth
                    />
                  </Grid>

                  {/* 삭제 버튼 + 기존 항목 표시 */}
                  <Grid item xs={12} md={3}>
                    <Box
                      display="flex"
                      justifyContent="flex-end"
                      alignItems="center"
                    >
                      <IconButton
                        color="error"
                        onClick={() => handleDeleteItem(itemData.id)}
                        disabled={items.length <= 1 || saving}
                      >
                        <DeleteIcon />
                      </IconButton>
                      {itemData.isExisting && (
                        <Chip
                          label="기존 항목"
                          size="small"
                          sx={{ ml: 1 }}
                          color="default"
                        />
                      )}
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            ))}
          </>
        )}
      </DialogContent>

      <Divider />

      <DialogActions sx={{ px: 3, py: 2, justifyContent: "flex-end" }}>
        <Button
          onClick={handleSave}
          variant="contained"
          color="primary"
          disabled={loading || saving}
          startIcon={saving ? <CircularProgress size={20} /> : null}
        >
          {saving ? "저장 중..." : "저장"}
        </Button>
        <Button onClick={onClose} variant="outlined" disabled={saving}>
          취소
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditModal;
